/**
 * <jgd-rotator> — turns its children into a horizontal scroll-snap carousel
 * (Embla principle). The layout itself is pure CSS (overflow-x + snap in the
 * top-bar slot stylesheet): the browser shows as many whole items as fit,
 * the rest is swipe/scrollable — no measuring against siblings, no mode
 * switching. This element only adds:
 *
 * - Autoplay: while the content overflows, every `speed` seconds it scrolls
 *   one item forward (back to the start after the last). No overflow → no
 *   autoplay. Paused while hovered, while the user touches/scrolls, while
 *   the tab is hidden, and entirely under prefers-reduced-motion.
 * - Edge fades: `data-fade-start` / `data-fade-end` attributes whenever
 *   content continues beyond an edge — the CSS mask fades cut-off items out
 *   softly.
 * - Theme editor: selecting a block scrolls its item into view and pauses
 *   autoplay until deselected.
 *
 * Markup contract: direct element children are the items (script/style tags
 * are ignored); `speed` attribute = seconds between steps (default 5).
 */
class JgdRotator extends HTMLElement {
  /** @type {AbortController} */
  #controller;

  /** @type {number | undefined} */
  #timer;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  #paused = false;

  /** 02.10.2026 (DBA): Rotator im Bild? Autoplay laeuft nur dann (vorher auch weggescrollt alle 5 s). */
  #imBild = true;

  /** @type {IntersectionObserver | undefined} */
  #sichtObserver;

  /** @type {IntersectionObserver | undefined} */
  #kantenObserver;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    // fresh controller per connect — a reconnected element must re-listen
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // 02.10.2026 (DBA): kein 'scroll'-Listener mehr -- er las bei jedem Scroll-Bild scrollLeft/scrollWidth und erzwang
    // damit Neuberechnungen der ganzen Seite. Die Fades koennen nur kippen, wenn das erste oder letzte Item seine
    // Sichtbarkeit im Rotator aendert; der IntersectionObserver meldet genau das, nach dem Layout (ohne Zwang).
    this.#kantenBeobachten();

    // Autoplay pausiert, solange der Nutzer interagiert.
    this.addEventListener('pointerenter', () => (this.#paused = true), { signal });
    this.addEventListener('pointerleave', () => (this.#paused = false), { signal });
    this.addEventListener('touchstart', () => (this.#paused = true), { signal, passive: true });
    this.addEventListener('touchend', () => (this.#paused = false), { signal, passive: true });

    // Größenänderungen (Viewport, async ladende Geschwister → Flex-Restplatz)
    // ändern nur, ob Überlauf existiert — die Fades hängen daran.
    this.#resizeObserver = new ResizeObserver(this.#updateEdges);
    this.#resizeObserver.observe(this);

    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
      this.addEventListener('shopify:block:deselect', () => (this.#paused = false), { signal });
      new MutationObserver(() => { this.#kantenBeobachten(); this.#updateEdges(); }).observe(this, { childList: true });
    }

    this.#updateEdges();

    // 02.10.2026 (DBA): Autoplay erst nach window.load starten und nur, solange der Rotator im Bild ist.
    this.#sichtObserver = new IntersectionObserver((eintraege) => {
      this.#imBild = eintraege[eintraege.length - 1].isIntersecting;
    });
    this.#sichtObserver.observe(this);
    const starten = () => {
      if (!this.isConnected || this.#timer) return;
      this.#timer = setInterval(this.#step, this.#interval);
    };
    if (document.readyState === 'complete') starten();
    else window.addEventListener('load', starten, { once: true, signal });
  }

  /** Erstes und letztes Item im Rotator beobachten (Ausloeser fuer #updateEdges). */
  #kantenBeobachten() {
    this.#kantenObserver?.disconnect();
    const items = this.#items;
    if (!items.length) return;
    this.#kantenObserver = new IntersectionObserver(this.#updateEdges, { root: this, threshold: [0, 1] });
    this.#kantenObserver.observe(items[0]);
    if (items.length > 1) this.#kantenObserver.observe(items[items.length - 1]);
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver?.disconnect();
    this.#sichtObserver?.disconnect();
    this.#kantenObserver?.disconnect();
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = undefined;
  }

  get #items() {
    return [...this.children].filter((el) => el.tagName !== 'SCRIPT' && el.tagName !== 'STYLE');
  }

  get #interval() {
    const seconds = Number(this.getAttribute('speed'));
    return (seconds > 0 ? seconds : 5) * 1000;
  }

  get #maxScroll() {
    return this.scrollWidth - this.clientWidth;
  }

  /** Kanten-Fades: nur dort einblenden, wo Inhalt weiterläuft. */
  #updateEdges = () => {
    this.toggleAttribute('data-fade-start', this.scrollLeft > 1);
    this.toggleAttribute('data-fade-end', this.#maxScroll - this.scrollLeft > 1);
  };

  /** Ein Item weiterscrollen; nach dem letzten zurück zum Anfang. */
  #step = () => {
    if (this.#paused || !this.#imBild || document.hidden || this.#reducedMotion.matches) return;
    if (this.#maxScroll <= 1) return; // alles sichtbar — kein Autoplay

    if (this.scrollLeft >= this.#maxScroll - 1) {
      this.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }

    // Erstes Item, das rechts der aktuellen linken Kante beginnt = nächste
    // Snap-Position (berücksichtigt den Flex-Gap automatisch).
    const base = this.getBoundingClientRect().left;
    const next = this.#items.find((item) => item.getBoundingClientRect().left - base > 1);
    const target = next ? this.scrollLeft + (next.getBoundingClientRect().left - base) : this.#maxScroll;
    this.scrollTo({ left: Math.min(target, this.#maxScroll), behavior: 'smooth' });
  };

  /**
   * Scroll to the item containing the block selected in the theme editor.
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const item = this.#items.find((el) => el === target || el.contains(target));
    if (!item) return;

    this.#paused = true;
    item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  };
}

if (!customElements.get('jgd-rotator')) {
  customElements.define('jgd-rotator', JgdRotator);
}
